import React, { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Theme, ColorScheme } from '../types'
import { CHROMATIC_COLORS, NEUTRAL_COLORS, ALL_PALETTE_COLORS, PaletteColor, lookupTailwindColor, SHADE_STEPS } from '../data/tailwindPalette'

export type ColorKey = keyof ColorScheme

export const COLOR_LABELS: { key: ColorKey; label: string; hint: string }[] = [
  { key: 'primary', label: 'Primary', hint: 'Buttons, links' },
  { key: 'secondary', label: 'Secondary', hint: 'Accents, badges' },
  { key: 'accent', label: 'Accent', hint: 'Highlights, CTAs' },
  { key: 'background', label: 'Background', hint: 'Page background' },
  { key: 'surface', label: 'Surface', hint: 'Cards, panels' },
  { key: 'text', label: 'Text', hint: 'Headings, body' },
  { key: 'textMuted', label: 'Muted', hint: 'Captions, labels' },
]

/* ============================================================
   TailwindColorPicker — Shared color picker used everywhere
   ============================================================ */

interface TailwindColorPickerProps {
  value: string
  onChange: (hex: string) => void
}

export function TailwindColorPicker({ value, onChange }: TailwindColorPickerProps) {
  const [open, setOpen] = useState(false)
  const [activeFamily, setActiveFamily] = useState<PaletteColor | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)

  // On open, pre-select the family matching the current value
  useEffect(() => {
    if (!open) {
      setActiveFamily(null)
      return
    }
    const match = lookupTailwindColor(value)
    if (match) {
      const found = ALL_PALETTE_COLORS.find(c => c.name === match.colorName)
      if (found) setActiveFamily(found)
    }
  }, [open])

  // Calculate fixed position when opening
  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return
    const rect = buttonRef.current.getBoundingClientRect()
    const panelW = 340
    const panelH = 380 // estimate max height

    let top = rect.bottom + 6
    let left = rect.right - panelW // align right edge to button right edge

    // Keep within viewport
    if (left < 8) left = 8
    if (left + panelW > window.innerWidth - 8) left = window.innerWidth - panelW - 8
    if (top + panelH > window.innerHeight - 8) {
      top = rect.top - panelH - 6 // flip above
    }

    setPos({ top, left })
  }, [])

  useEffect(() => {
    if (!open) return
    updatePosition()
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [open, updatePosition])

  // Close on click outside
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        buttonRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open])

  const twMatch = lookupTailwindColor(value)

  const selectColor = (hex: string) => {
    onChange(hex)
    setOpen(false)
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 border border-gray-200 rounded-md px-1.5 py-1 hover:border-gray-300 transition-colors cursor-pointer"
      >
        <div
          className="w-5 h-5 rounded border border-gray-200 flex-shrink-0"
          style={{ backgroundColor: value }}
        />
        <span className="text-[10px] font-mono text-gray-600 uppercase w-[40px] truncate">{value}</span>
      </button>

      {open && pos && createPortal(
        <div
          ref={panelRef}
          className="fixed z-[9999] bg-white border border-gray-200 rounded-xl shadow-2xl"
          style={{ top: pos.top, left: pos.left, width: 340 }}
        >
          {/* Header — current value */}
          <div className="flex items-center justify-between px-4 pt-3 pb-2">
            <div className="flex items-center gap-2">
              <div
                className="w-6 h-6 rounded-md border border-gray-200"
                style={{ backgroundColor: value }}
              />
              <span className="text-sm font-medium text-gray-900">
                {twMatch ? `${twMatch.colorName} ${twMatch.shade}` : 'Custom'}
              </span>
            </div>
            <span className="text-[11px] font-mono text-gray-400 uppercase">{value}</span>
          </div>

          <div className="px-4 pb-3">
            {/* Color family grid */}
            <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-2">Color</p>
            <div className="grid grid-cols-9 gap-1.5 mb-1">
              {CHROMATIC_COLORS.map((color) => {
                const isActive = activeFamily?.name === color.name
                return (
                  <button
                    key={color.name}
                    type="button"
                    onClick={() => setActiveFamily(color)}
                    title={color.name}
                    className={`aspect-square rounded-lg cursor-pointer transition-all ${
                      isActive
                        ? 'ring-2 ring-gray-900 ring-offset-2 scale-105'
                        : 'hover:ring-2 hover:ring-gray-300 hover:ring-offset-1 hover:scale-105'
                    }`}
                    style={{ backgroundColor: color.shades[500] }}
                  />
                )
              })}
            </div>

            {/* Neutrals + B&W */}
            <div className="grid grid-cols-9 gap-1.5 mb-4">
              {NEUTRAL_COLORS.map((color) => {
                const isActive = activeFamily?.name === color.name
                return (
                  <button
                    key={color.name}
                    type="button"
                    onClick={() => setActiveFamily(color)}
                    title={color.name}
                    className={`aspect-square rounded-lg cursor-pointer transition-all border ${
                      isActive
                        ? 'ring-2 ring-gray-900 ring-offset-2 scale-105 border-transparent'
                        : 'border-gray-200 hover:ring-2 hover:ring-gray-300 hover:ring-offset-1 hover:scale-105'
                    }`}
                    style={{ backgroundColor: color.shades[500] }}
                  />
                )
              })}
              <button
                type="button"
                onClick={() => selectColor('#ffffff')}
                title="White"
                className={`aspect-square rounded-lg cursor-pointer transition-all border ${
                  value.toLowerCase() === '#ffffff'
                    ? 'ring-2 ring-gray-900 ring-offset-2 scale-105 border-transparent'
                    : 'border-gray-200 hover:ring-2 hover:ring-gray-300 hover:ring-offset-1 hover:scale-105'
                }`}
                style={{ backgroundColor: '#ffffff' }}
              />
              <button
                type="button"
                onClick={() => selectColor('#000000')}
                title="Black"
                className={`aspect-square rounded-lg cursor-pointer transition-all border ${
                  value.toLowerCase() === '#000000'
                    ? 'ring-2 ring-gray-900 ring-offset-2 scale-105 border-transparent'
                    : 'border-gray-600 hover:ring-2 hover:ring-gray-300 hover:ring-offset-1 hover:scale-105'
                }`}
                style={{ backgroundColor: '#000000' }}
              />
            </div>

            {/* Shade picker */}
            {activeFamily ? (
              <div>
                <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-2">
                  {activeFamily.name} — Shade
                </p>
                <div className="grid grid-cols-11 gap-1">
                  {SHADE_STEPS.map((shade) => {
                    const hex = activeFamily.shades[shade]
                    const isSelected = hex.toLowerCase() === value.toLowerCase()
                    const isLight = shade <= 100
                    return (
                      <button
                        key={shade}
                        type="button"
                        onClick={() => selectColor(hex)}
                        title={`${shade}`}
                        className={`aspect-square rounded-lg cursor-pointer transition-all border ${
                          isSelected
                            ? 'ring-2 ring-gray-900 ring-offset-2 scale-110'
                            : 'hover:ring-2 hover:ring-gray-300 hover:ring-offset-1 hover:scale-105'
                        } ${isLight ? 'border-gray-200' : 'border-transparent'}`}
                        style={{ backgroundColor: hex }}
                      />
                    )
                  })}
                </div>
                <div className="flex justify-between mt-1.5 px-0.5">
                  <span className="text-[9px] text-gray-300">50</span>
                  <span className="text-[9px] text-gray-300">500</span>
                  <span className="text-[9px] text-gray-300">950</span>
                </div>
              </div>
            ) : (
              <div className="py-3 text-center">
                <p className="text-xs text-gray-400">Select a color above to choose a shade</p>
              </div>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}

// Keep legacy name for existing imports
export const InlineColorPicker = TailwindColorPicker

/* ============================================================
   ColorTableEditor — The light/dark grid used in both
   the theme editor sidebar and the create wizard
   ============================================================ */

export function ColorTableEditor({
  lightMode,
  darkMode,
  onColorChange,
}: {
  lightMode: ColorScheme
  darkMode: ColorScheme
  onColorChange: (mode: 'lightMode' | 'darkMode', key: ColorKey, value: string) => void
}) {
  return (
    <div className="p-4 space-y-1">
      <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 items-center px-1 pb-2 border-b border-gray-100">
        <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Color</span>
        <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider w-[72px] text-center">
          <span className="inline-flex items-center gap-1">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            Light
          </span>
        </span>
        <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider w-[72px] text-center">
          <span className="inline-flex items-center gap-1">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
            Dark
          </span>
        </span>
      </div>
      {COLOR_LABELS.map(({ key, label, hint }) => (
        <div
          key={key}
          className="grid grid-cols-[1fr_auto_auto] gap-x-3 items-center py-2 px-1 rounded-md hover:bg-gray-50 transition-colors"
        >
          <div>
            <span className="text-xs font-medium text-gray-700">{label}</span>
            <span className="block text-[10px] text-gray-400">{hint}</span>
          </div>
          <TailwindColorPicker
            value={lightMode[key]}
            onChange={(v) => onColorChange('lightMode', key, v)}
          />
          <TailwindColorPicker
            value={darkMode[key]}
            onChange={(v) => onColorChange('darkMode', key, v)}
          />
        </div>
      ))}
    </div>
  )
}

/* ============================================================
   ColorEditor — Used in the theme editor sidebar
   (wraps ColorTableEditor with a Theme object)
   ============================================================ */

interface ColorEditorProps {
  theme: Theme
  onColorChange: (mode: 'lightMode' | 'darkMode', key: ColorKey, value: string) => void
}

export function ColorEditor({ theme, onColorChange }: ColorEditorProps) {
  return (
    <ColorTableEditor
      lightMode={theme.lightMode}
      darkMode={theme.darkMode}
      onColorChange={onColorChange}
    />
  )
}
